/**
 * RWBR — "DOWNLOAD MY LEAGUE" / "OPEN A LEAGUE FILE". D-EZ-99 (c).
 *
 * A league as a file the player keeps: the same head, seasons and races the
 * account save stores (`leagueSlot.packLeague`), in one gzipped JSON document.
 * Opening one goes through the same `unpackLeague` checks as a cloud load, so a
 * file from another league, an older version or a damaged download is refused
 * in one plain sentence rather than half-opened.
 *
 * Nothing here touches a network. The browser writes the file and reads it.
 */

import {
  compressionAvailable,
  gunzipJson,
  gzipJson,
  OLD_SAVE_MESSAGE,
  packLeague,
  readLeagueHead,
  readSeasonRaceRow,
  SLOT_FORMAT_VERSION,
  SLOT_OLDEST_READABLE,
  unpackLeague,
  type LeagueHead,
  type LeagueToSave,
  type LoadedLeague,
  type PackedSeason,
  type SlotFailure,
} from './leagueSlot.ts';
import type { SeasonRaceChunk } from './seasonSlot.ts';

/** What the file says it is, so a random JSON file is refused by name. */
export const LEAGUE_FILE_KIND = 'racing-slop-league';
export const LEAGUE_FILE_EXTENSION = '.rsleague';
export const LEAGUE_FILE_MIME = 'application/gzip';

export interface LeagueFile {
  kind: typeof LEAGUE_FILE_KIND;
  v: number;
  exportedAt: string;
  head: LeagueHead;
  seasons: PackedSeason[];
  races: SeasonRaceChunk[];
}

/** Pure. The league on the workbench, as a file document. */
export function leagueFileOf(league: LeagueToSave, now: Date = new Date()): LeagueFile | SlotFailure {
  const pack = packLeague(league);
  if ('ok' in pack) return pack;
  return {
    kind: LEAGUE_FILE_KIND,
    v: SLOT_FORMAT_VERSION,
    exportedAt: now.toISOString(),
    head: pack.head,
    seasons: pack.seasons.map((s) => s.pack()),
    races: pack.races,
  };
}

/** A file name a person can recognise: `RASCAL-season-3-race-12.rsleague`. */
export function leagueFileName(head: Pick<LeagueHead, 'leagueName' | 'seasonNumber' | 'kind'> & { racesRun?: number }): string {
  const name = head.leagueName.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'league';
  const where = head.kind === 'season' ? `-race-${head.racesRun ?? 0}` : '-finished';
  return `${name}-season-${head.seasonNumber}${where}${LEAGUE_FILE_EXTENSION}`;
}

/** The league as gzipped bytes, ready to hand the browser as a download. */
export async function encodeLeagueFile(league: LeagueToSave): Promise<{ bytes: Uint8Array; name: string } | SlotFailure> {
  const file = leagueFileOf(league);
  if ('ok' in file) return file;
  if (!compressionAvailable()) {
    return { ok: false, code: 'unsupported-browser', message: 'This browser is too old to write a league file.' };
  }
  const head = file.head;
  return {
    bytes: await gzipJson(file),
    name: leagueFileName({ ...head, racesRun: head.kind === 'season' ? head.racesRun : undefined }),
  };
}

/** Pure-ish. A parsed file document, checked and opened. */
export function openLeagueDocument(doc: unknown): LoadedLeague | SlotFailure {
  if (!doc || typeof doc !== 'object' || (doc as { kind?: unknown }).kind !== LEAGUE_FILE_KIND) {
    return { ok: false, code: 'corrupt', message: "That file isn't a Racing Slop league." };
  }
  const f = doc as Partial<LeagueFile>;
  if (typeof f.v !== 'number' || f.v < SLOT_OLDEST_READABLE) return { ok: false, code: 'format-too-old', message: OLD_SAVE_MESSAGE };
  if (f.v > SLOT_FORMAT_VERSION) {
    return { ok: false, code: 'format-too-new', message: 'That league file was made by a newer version of the game. Reload the page to pick it up.' };
  }
  const head = readLeagueHead(f.head);
  if ('ok' in head) return head;
  const races: SeasonRaceChunk[] = [];
  for (const [i, raw] of (Array.isArray(f.races) ? f.races : []).entries()) {
    const chunk = readSeasonRaceRow(raw, i + 1);
    if ('ok' in chunk) return chunk;
    races.push(chunk);
  }
  return unpackLeague(head, Array.isArray(f.seasons) ? f.seasons : [], races, f.exportedAt ?? '');
}

/** Bytes from a chosen file — gzipped (as written here) or plain JSON. */
export async function readLeagueFile(bytes: Uint8Array): Promise<LoadedLeague | SlotFailure> {
  let doc: unknown;
  if (bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b) {
    const unpacked = await gunzipJson(bytes);
    if ('ok' in unpacked) return { ...unpacked, message: "That league file is damaged and couldn't be opened." };
    doc = unpacked.value;
  } else {
    try {
      doc = JSON.parse(new TextDecoder().decode(bytes));
    } catch {
      return { ok: false, code: 'corrupt', message: "That file isn't a Racing Slop league." };
    }
  }
  return openLeagueDocument(doc);
}

/** Browser only: hand the bytes to the player as a download. */
export function downloadBytes(bytes: Uint8Array, name: string, mime = LEAGUE_FILE_MIME): void {
  const blob = new Blob([bytes as BlobPart], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
